import type { ShareTier } from '@garnet/domain';
import type { SharedCheck } from './types.js';

/**
 * Pure data-shaping for the portal — no React, no I/O, no clock (CONVENTIONS).
 * Unit-tested directly in shape.test.ts.
 */

// ── Three-tier disclosure (PRD §11) ──────────────────────────────────────────
const TIER_RANK: Record<ShareTier, number> = { public: 0, restricted: 1, authority: 2 };

export function tierRank(tier: ShareTier): number {
  return TIER_RANK[tier];
}

/** Checks a holder of `granted` tier may see: those whose minimum tier is at or below it. */
export function visibleAtTier(checks: readonly SharedCheck[], granted: ShareTier): SharedCheck[] {
  return checks.filter((c) => tierRank(c.min_tier) <= tierRank(granted));
}

/** How many disclosed fields were withheld at this tier — surfaced so the gap is honest. */
export function withheldCount(checks: readonly SharedCheck[], granted: ShareTier): number {
  return checks.length - visibleAtTier(checks, granted).length;
}

// ── Live identifier parse + confirm-before-trust (PRD §27.2 "claim under test") ──
export interface ParsedSegment {
  ai: string;
  label: string;
  value: string;
  confidence: number;
}

export interface ParsedIdentifier {
  raw: string;
  recognized: boolean;
  /** The device identifier (GS1 AI 01 / a bare GTIN-ish run). */
  di: ParsedSegment | null;
  /** Production identifiers — lot, expiry, serial (AIs 10/17/21). */
  pi: ParsedSegment[];
}

const AI_LABELS: Record<string, string> = {
  '01': 'GTIN / DI',
  '17': 'Expiry',
  '10': 'Lot',
  '21': 'Serial',
};
const DI_AIS = new Set(['01']);

/**
 * Parse a GS1 carrier string. Accepts the human "(01)…(21)…" application-identifier
 * form, or — when no AIs are present — treats a bare 12–14 digit run as a probable GTIN.
 * Confidence is structural (recognized AI vs heuristic), never asserted as truth: the UI
 * confirms each field before it is trusted.
 */
export function parseIdentifier(raw: string): ParsedIdentifier {
  const trimmed = raw.trim();
  if (trimmed === '') return { raw: '', recognized: false, di: null, pi: [] };

  const aiMatches = [...trimmed.matchAll(/\((\d{2,4})\)([^()]*)/g)];
  if (aiMatches.length > 0) {
    let di: ParsedSegment | null = null;
    const pi: ParsedSegment[] = [];
    for (const m of aiMatches) {
      const ai = m[1] ?? '';
      const value = (m[2] ?? '').trim();
      if (value === '') continue;
      const seg: ParsedSegment = { ai, label: AI_LABELS[ai] ?? `AI ${ai}`, value, confidence: 0.97 };
      if (DI_AIS.has(ai)) di = seg;
      else pi.push(seg);
    }
    return { raw: trimmed, recognized: di !== null || pi.length > 0, di, pi };
  }

  // No AIs — heuristic: a bare digit run is a probable GTIN/DI at lower confidence.
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 12 && digits.length <= 14) {
    return {
      raw: trimmed,
      recognized: false,
      di: { ai: '01', label: AI_LABELS['01'] ?? 'GTIN / DI', value: digits, confidence: 0.62 },
      pi: [],
    };
  }
  return { raw: trimmed, recognized: false, di: null, pi: [] };
}
